import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import moment from 'moment';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * AggregatedAssetExpenseChart
 * - Accepts a list of assetIds (e.g., property structures + land)
 * - Fetches maintenance-items for each asset and aggregates Actual vs Expected expenses over time
 * - Provides same look/feel as AssetExpenseChart
 */
const AggregatedAssetExpenseChart = ({ assetIds = [], budgetAssetId = null, allowedBudgetParents = null, chartTitle = 'Aggregated Property Expenses', extraRightControls = null, onRangeChange = null, onBudgetTableChange = null, onBarSelectionChange = null, preferAssetExpenseTypes = false, showExpected = false, onToggleExpected = null, groupByGroup = false }) => {
  const [showSubcats, setShowSubcats] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [showProjectedRecurring, setShowProjectedRecurring] = useState(false);
  const [cumulative, setCumulative] = useState(false);
  // Optional: internal toggle to override and show high-level groups (Financing, Operating, CapEx)
  const [useGroups, setUseGroups] = useState(groupByGroup);
  const rollupHome = false;

  useEffect(() => {
    setUseGroups(groupByGroup);
  }, [groupByGroup]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actuals, setActuals] = useState([]);
  const [projected, setProjected] = useState([]);
  const [aggregation, setAggregation] = useState('monthly');

  // Date range states: default YTD (Jan 1 of current year to today)
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    const fetchAll = async () => {
      if (!assetIds || assetIds.length === 0) {
        setActuals([]);
        setProjected([]);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const results = await Promise.allSettled(
          assetIds.map((id) => axios.get(`/api/balance_sheet/assets/physical_assets/${id}/linked-expenses/`, {
            params: {
              start_date: fromDate,
              end_date: toDate,
              only_txn_backed: 1,
              // When used by the Vehicle page, include derived AssetExpense rows for EV charging (asset_input)
              include_asset_input: preferAssetExpenseTypes ? 1 : 0,
            }
          }))
        );
        const a = [];
        const p = [];
        for (const r of results) {
          if (r.status === 'fulfilled') {
            if (Array.isArray(r.value?.data?.actual)) a.push(...r.value.data.actual);
            if (Array.isArray(r.value?.data?.projected)) p.push(...r.value.data.projected);
          }
        }
        setActuals(a);
        setProjected(p);
      } catch (e) {
        setError('Failed to load aggregated expenses.');
        setActuals([]);
        setProjected([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [assetIds, fromDate, toDate, preferAssetExpenseTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const [hiddenSubcats, setHiddenSubcats] = useState(new Set());
  // Multi-select bar indices (Set)
  const [selectedBarIndices, setSelectedBarIndices] = useState(new Set());
  const chartRef = useRef(null);

  useEffect(() => {
    setHiddenSubcats(new Set());
  }, [showSubcats, useGroups, showRecurring, assetIds]);

  // Reset bar selection when date range or aggregation changes
  useEffect(() => {
    setSelectedBarIndices(new Set());
  }, [fromDate, toDate, aggregation]);

  // Budget data
  const [budgetData, setBudgetData] = useState([]);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [categoryHierarchy, setCategoryHierarchy] = useState(null);

  useEffect(() => {
    if (!budgetAssetId) { setBudgetData([]); return; }
    setBudgetLoading(true);
    axios.get('/api/income_statement/budgets/asset-budget/', { params: { asset_id: budgetAssetId } })
      .then(res => setBudgetData(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBudgetData([]))
      .finally(() => setBudgetLoading(false));
  }, [budgetAssetId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/')
      .then(res => {
        const cats = Array.isArray(res.data) ? res.data : [];
        const parents = {};
        const labelsMap = {};
        cats.forEach(c => {
          labelsMap[c.value] = c.label;
          if (c.children && c.children.length > 0) {
            parents[c.value] = { label: c.label, children: c.children };
            c.children.forEach(ch => { labelsMap[ch.value] = ch.label; });
          }
        });
        setCategoryHierarchy({ parents, labels: labelsMap });
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { labels, actualSeries, expectedSeries, actualBreakdown, expectedBreakdown, subBreakActual, subBreakExpected, subcats, dateRange, stats } = useMemo(() => {
    const dateRange = [];
    let start = moment(fromDate);
    const end = moment(toDate);
    while (start.isSameOrBefore(end)) {
      dateRange.push(start.format('YYYY-MM-DD'));
      if (aggregation === 'daily') start.add(1, 'day');
      else if (aggregation === 'weekly') start.add(1, 'week');
      else if (aggregation === 'monthly') start.add(1, 'month');
      else if (aggregation === 'yearly') start.add(1, 'year');
      else start.add(1, 'day');
    }

    if ((!actuals || actuals.length === 0) && (!projected || projected.length === 0)) {
      return {
        labels: dateRange,
        actualSeries: dateRange.map(() => 0),
        expectedSeries: dateRange.map(() => 0),
        actualBreakdown: {},
        expectedBreakdown: {},
        subBreakActual: {},
        subBreakExpected: {},
        subcats: [],
        dateRange,
        stats: { sum: 0, avg: 0, stdDev: 0 }
      };
    }

    const actualsMap = {};
    const expectedsMap = {};
    const actualBreakdown = {}; // {period: {category: sum}}
    const expectedBreakdown = {};

    const getPeriod = () => {
      if (aggregation === 'weekly') return 'week';
      if (aggregation === 'monthly') return 'month';
      if (aggregation === 'yearly') return 'year';
      return 'day';
    };

    // Normalize a category-ish string into a canonical token (lowercase, underscores)
    const canon = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const isUtilitiesLike = (s) => {
      const c = canon(s);
      if (!c) return false;
      if (c.startsWith('utilities')) return true;
      // Known aliases that should roll up under Utilities
      const aliases = new Set([
        'water','electric','electricity','electric_gas','electric___gas','utilities__water','utilities__electric','utilities__electric_gas','utilities__other','other_utilities'
      ]);
      return aliases.has(c);
    };

    const isHomeLike = (s) => {
      const c = canon(s);
      return !!c && (c === 'home' || c.startsWith('home_'));
    };

    // Mirror backend HOME_TXN_TO_ASSET_CATEGORY mapping to unify keys in charts
    const homeTxnToAssetMap = {
      'home_pest_control': 'home_pest_control',
      'home_decor': 'home_decor',
      'home_furniture': 'home_furniture',
      'home_artwork': 'home_artwork',
      'home_inspection': 'home_inspection',
      'home_maintenance': 'home_maintenance',
      'home_repair': 'home_repair',
      'home_enhancement': 'home_enhancement',
      'home_improvement': 'home_enhancement',
      'home_tax_payment': 'home_tax_payment',
      'home_insurance_payment': 'home_insurance_payment',
      'home_plants': 'home_plants',
      'vehicle_licensing_tags': 'licensing_tags',
    };

    const mapHomeTxnToAsset = (c) => {
      const key = canon(c);
      return homeTxnToAssetMap[key] || null;
    };

    // Map vehicle transaction categories to their budget/asset expense slugs
    const vehicleTxnToAssetMap = {
      'vehicle_licensing_tags': 'licensing_tags',       // maps txn → asset slug
      'automotive_fuel_charging': 'asset_input',
      'automotive_car_wash': 'cleaning',
      'automotive_parking': 'asset_storage',
      'automotive_other': 'other',
    };

    // Map bare AssetExpenseORM expense_category slugs → automotive_* child slugs.
    // Used when txn_category is the generic 'automotive' parent and asset category is a bare slug.
    // Entries marked VEHICLE_ONLY are safe to apply even without a txn_category because they
    // cannot appear on home/property expenses.
    const vehicleAssetToAutomotiveMap = {
      'maintenance': 'automotive_maintenance',       // ambiguous — only applied when cTxn==='automotive'
      'vehicle_maintenance': 'automotive_maintenance', // VEHICLE_ONLY
      'repair': 'automotive_repair',                 // ambiguous — only applied when cTxn==='automotive'
      'vehicle_repair': 'automotive_repair',          // VEHICLE_ONLY
      'insurance': 'automotive_insurance',            // ambiguous — only applied when cTxn==='automotive'
      'cleaning': 'automotive_car_wash',              // ambiguous — only applied when cTxn==='automotive'
      'asset_storage': 'automotive_parking',          // ambiguous — only applied when cTxn==='automotive'
      'asset_input': 'automotive_fuel_charging',      // ambiguous — only applied when cTxn==='automotive'
      'licensing_tags': 'vehicle_licensing_tags',    // VEHICLE_ONLY
      'vehicle_licensing_tags': 'vehicle_licensing_tags', // VEHICLE_ONLY
      'vehicle_enhancement': 'automotive_other',      // VEHICLE_ONLY
      'vehicle_other': 'automotive_other',            // VEHICLE_ONLY
      'ticket_or_fine': 'automotive_other',           // VEHICLE_ONLY
    };
    // Vehicle-only slugs: safe to map to automotive_* even without a txn_category
    const vehicleOnlySlugs = new Set([
      'vehicle_maintenance','vehicle_repair','licensing_tags','vehicle_licensing_tags',
      'vehicle_enhancement','vehicle_other','ticket_or_fine',
    ]);

    const mapVehicleTxnToAsset = (c) => {
      const key = canon(c);
      return vehicleTxnToAssetMap[key] || null;
    };

    const catKey = (it) => {
      if (showRecurring) {
        return it.recurring ? 'recurring' : 'non_recurring';
      }
      if (useGroups && it.expense_group) {
        return it.expense_group;
      }
      const txnCat = String(it?.txn_category || '');
      const assetType = String(it?.expense_category || ''); // legacy/secondary
      const assetCategory = String(it?.asset_category || ''); // canonical from API when present
      const generic = String(it?.category || '');
      const cTxn = canon(txnCat);
      const cAssetCanonical = canon(assetCategory);
      const cAsset = canon(assetType);
      const cGen = canon(generic);
      // Debt deduplication: financing_principal, financing_interest, and the 'payments' transaction
      // category are all sub-components of the total debt payment; map them all to debt_payment so
      // they are never shown separately and always roll up into the single "Debt Payment" bar.
      const DEBT_ROLLUP_SLUGS = new Set(['financing_principal','principal','financing_interest','interest','payments']);
      if (DEBT_ROLLUP_SLUGS.has(cAsset) || DEBT_ROLLUP_SLUGS.has(cTxn) || DEBT_ROLLUP_SLUGS.has(cAssetCanonical)) {
        return 'debt_payment';
      }
      // If caller wants asset expense types (e.g., property/vehicle pages), prefer them BUT
      // always collapse Utilities subcategories to the parent 'utilities' when hinted by txn/generic.
      if (preferAssetExpenseTypes) {
        // Normalize Utilities: preserve child slug (e.g. utilities__water) but collapse bare aliases to parent
        // Only treat as utilities if the asset/expense category is NOT a home category
        if ((isUtilitiesLike(cTxn) || isUtilitiesLike(cGen)) && !isHomeLike(cAsset) && !isHomeLike(cAssetCanonical)) {
          // Strip parent prefix so child slug matches bare budget keys (e.g. utilities__water → water)
          const stripUtilPrefix = (s) => {
            if (!s || s === 'utilities') return null;
            if (s.startsWith('utilities__')) return s.slice('utilities__'.length);
            if (s.startsWith('utilities_')) return s.slice('utilities_'.length);
            return s;
          };
          const stripped = stripUtilPrefix(txnCat) || stripUtilPrefix(generic);
          if (stripped) return stripped;
          return 'utilities';
        }
        // Prefer canonical asset_category first when available
        if (cAssetCanonical) {
          if (rollupHome && isHomeLike(cAssetCanonical)) return 'home';
          // Legacy bad slugs on asset_category too (API sets asset_category = e.expense_category)
          const LEGACY_BAD_SLUGS_C = new Set(['utilities_trash','utilities_garbage','utilities_sewer','utilities_recycling']);
          if (LEGACY_BAD_SLUGS_C.has(cAssetCanonical) && cTxn && cTxn !== 'none' && !isUtilitiesLike(cTxn)) {
            return cTxn;
          }
          // If asset_category is a generic parent but txn_category is a known WM child, prefer child
          const WM_CHILDREN_C = new Set(['sanitation','recycling','yard_waste']);
          if (WM_CHILDREN_C.has(cTxn) && (cAssetCanonical === 'utilities' || cAssetCanonical === 'maintenance' || cAssetCanonical === 'waste_management')) {
            return cTxn;
          }
          // If asset_category is a bare child slug (e.g. 'maintenance', 'repair') but txn_category
          // has the full home-prefixed version (e.g. 'home_maintenance', 'home_repair'), prefer the full slug
          if (cTxn && cTxn === 'home_' + cAssetCanonical) return cTxn;
          // If txn_category is the generic 'automotive' parent, map bare asset slug to automotive_* child
          if (cTxn === 'automotive' && vehicleAssetToAutomotiveMap[cAssetCanonical]) return vehicleAssetToAutomotiveMap[cAssetCanonical];
          // Vehicle-only slugs: safe to map to automotive_* even without a txn_category
          if (vehicleOnlySlugs.has(cAssetCanonical) && vehicleAssetToAutomotiveMap[cAssetCanonical]) return vehicleAssetToAutomotiveMap[cAssetCanonical];
          return cAssetCanonical;
        }
        if (cAsset) {
          // Optional roll-up: group Home subcategories as one when toggle is on
          if (rollupHome && isHomeLike(cAsset)) return 'home';
          // If asset category is a parent slug (e.g. 'waste_management'), try to get a more
          // specific child slug from txn_category before returning the generic parent
          if (cAsset === 'waste_management') {
            const stripWmPrefixEarly = (s) => {
              if (!s || s === 'waste_management') return null;
              if (s.startsWith('waste_management__')) return s.slice('waste_management__'.length);
              if (s.startsWith('waste_management_')) return s.slice('waste_management_'.length);
              return (s !== 'waste_management') ? s : null;
            };
            const wmChild = stripWmPrefixEarly(cTxn) || stripWmPrefixEarly(cGen);
            if (wmChild) return wmChild;
          }
          // Legacy bad slugs like 'utilities_trash' — if txn_category has a valid non-utility
          // child slug (e.g. 'sanitation'), prefer it over the stale asset expense_category
          const LEGACY_BAD_SLUGS = new Set(['utilities_trash','utilities_garbage','utilities_sewer','utilities_recycling']);
          if (LEGACY_BAD_SLUGS.has(cAsset) && cTxn && cTxn !== 'none' && !isUtilitiesLike(cTxn)) {
            return cTxn;
          }
          // If asset expense_category is a generic parent (e.g. 'utilities', 'maintenance') but
          // txn_category is a known waste_management child (e.g. 'sanitation'), prefer the child
          const WM_CHILDREN = new Set(['sanitation','recycling','yard_waste']);
          if (WM_CHILDREN.has(cTxn) && (cAsset === 'utilities' || cAsset === 'maintenance' || cAsset === 'waste_management')) {
            return cTxn;
          }
          // If asset_category is a bare child slug (e.g. 'maintenance', 'repair') but txn_category
          // has the full home-prefixed version (e.g. 'home_maintenance', 'home_repair'), prefer the
          // full slug so it matches budget keys like 'home_maintenance'/'home_repair' under Home parent.
          if (cTxn && cTxn === 'home_' + cAsset) return cTxn;
          // If txn_category is the generic 'automotive' parent, map bare asset slug to automotive_* child
          if (cTxn === 'automotive' && vehicleAssetToAutomotiveMap[cAsset]) return vehicleAssetToAutomotiveMap[cAsset];
          // Vehicle-only slugs: safe to map to automotive_* even without a txn_category
          if (vehicleOnlySlugs.has(cAsset) && vehicleAssetToAutomotiveMap[cAsset]) return vehicleAssetToAutomotiveMap[cAsset];
          return cAsset;
        }
        // If no explicit asset category, try mapping vehicle txn categories to budget asset slugs
        const vehicleMapped = mapVehicleTxnToAsset(cTxn) || mapVehicleTxnToAsset(cGen);
        if (vehicleMapped) return vehicleMapped;
        // Try mapping Home txn/generic to canonical asset category code
        const mapped = mapHomeTxnToAsset(cTxn) || mapHomeTxnToAsset(cGen);
        if (mapped) {
          if (rollupHome && isHomeLike(mapped)) return 'home';
          return mapped;
        }
        if (cTxn === 'automotive' && cAsset) return vehicleAssetToAutomotiveMap[cAsset] || cAsset; // safety
        // Strip waste_management parent prefix so child slug matches bare budget keys
        const stripWmPrefix = (s) => {
          if (!s || s === 'waste_management') return null;
          if (s.startsWith('waste_management__')) return s.slice('waste_management__'.length);
          if (s.startsWith('waste_management_')) return s.slice('waste_management_'.length);
          return null;
        };
        const wmStripped = stripWmPrefix(cTxn) || stripWmPrefix(cGen);
        if (wmStripped) return wmStripped;
        return cGen || cTxn || 'uncategorized';
      }
      // Default: previous behavior prioritizing txn_category, but normalize utilities parent grouping as well
      if (isUtilitiesLike(cTxn) || isUtilitiesLike(cGen)) {
        // If txn_category is a specific child slug, return the raw value so it matches budget slugs
        if (txnCat && txnCat !== 'utilities' && canon(txnCat).startsWith('utilities')) return txnCat;
        if (generic && generic !== 'utilities' && canon(generic).startsWith('utilities')) return generic;
        return 'utilities';
      }
      if (cTxn === 'automotive' && cAsset) return cAsset;
      // Optional roll-up for Home on txn/generic side as a fallback
      const chosen = cTxn || cAsset || cGen || 'uncategorized';
      if (rollupHome && isHomeLike(chosen)) return 'home';
      return chosen;
    };

    (actuals || []).forEach((it) => {
      if (!it?.date || it.amount == null) return;
      const dateKey = moment(it.date).startOf(getPeriod()).format('YYYY-MM-DD');
      const amt = Number(it.amount) || 0;
      actualsMap[dateKey] = (actualsMap[dateKey] || 0) + amt;
      const ck = catKey(it);
      actualBreakdown[dateKey] = actualBreakdown[dateKey] || {};
      actualBreakdown[dateKey][ck] = (actualBreakdown[dateKey][ck] || 0) + amt;
    });

    // Projected recurring expenses (if enabled)
    if (showProjectedRecurring) {
      (projected || []).forEach((it) => {
        // Only include those coming from a recurring series for this specific toggle
        if (!it?.date || it.amount == null || !it.recurring_name) return;
        const dateKey = moment(it.date).startOf(getPeriod()).format('YYYY-MM-DD');
        const amt = Number(it.amount) || 0;
        expectedsMap[dateKey] = (expectedsMap[dateKey] || 0) + amt;
        const ck = catKey(it);
        expectedBreakdown[dateKey] = expectedBreakdown[dateKey] || {};
        expectedBreakdown[dateKey][ck] = (expectedBreakdown[dateKey][ck] || 0) + amt;
      });
    }

    if (showExpected) {
      (projected || []).forEach((it) => {
        // showExpected typically refers to non-recurring expected items (like maintenance schedule)
        // or just all projected by default. But here the user explicitly asked for "Show Projected Recurring".
        // If we have other projected items NOT from recurring, they go here.
        if (!it?.date || it.amount == null || it.recurring_name) return; 
        const dateKey = moment(it.date).startOf(getPeriod()).format('YYYY-MM-DD');
        const amt = Number(it.amount) || 0;
        expectedsMap[dateKey] = (expectedsMap[dateKey] || 0) + amt;
        const ck = catKey(it);
        expectedBreakdown[dateKey] = expectedBreakdown[dateKey] || {};
        expectedBreakdown[dateKey][ck] = (expectedBreakdown[dateKey][ck] || 0) + amt;
      });
    }

    const actualSeries = dateRange.map((d) => actualsMap[d] || 0);
    const expectedSeries = dateRange.map((d) => expectedsMap[d] || 0);

    // Build per-subcategory arrays aligned to dateRange
    const subcatSet = new Set();
    Object.values(actualBreakdown).forEach(m => Object.keys(m).forEach(k => subcatSet.add(k)));
    Object.values(expectedBreakdown).forEach(m => Object.keys(m).forEach(k => subcatSet.add(k)));
    const subcats = Array.from(subcatSet);

    const subBreakActual = {}; // {subcat: [vals per label]}
    const subBreakExpected = {};
    subcats.forEach((cat) => {
      subBreakActual[cat] = dateRange.map(d => (actualBreakdown[d]?.[cat] || 0));
      subBreakExpected[cat] = dateRange.map(d => (expectedBreakdown[d]?.[cat] || 0));
    });

    // Stats calculation (Sum, Avg, StdDev) for Actuals - Only include visible subcats if in stacked/subcat mode
    let actualValues = [];
    const isStacked = showSubcats || useGroups || showRecurring;
    if (isStacked) {
      // For each period, sum only the values of visible categories
      dateRange.forEach(d => {
        let periodSum = 0;
        const breakDown = actualBreakdown[d] || {};
        Object.entries(breakDown).forEach(([cat, val]) => {
          if (!hiddenSubcats.has(cat)) {
            periodSum += Number(val) || 0;
          }
        });
        actualValues.push(periodSum);
      });
    } else {
      // Default: use the full actualSeries if no special grouping is active
      actualValues = hiddenSubcats.has('__ALL_ACTUAL__') ? dateRange.map(() => 0) : actualSeries;
    }

    const sum = actualValues.reduce((a, b) => a + b, 0);
    const avg = actualValues.length > 0 ? sum / actualValues.length : 0;
    const stdDev = actualValues.length > 1
      ? Math.sqrt(actualValues.map(v => Math.pow(v - avg, 2)).reduce((a, b) => a + b, 0) / actualValues.length)
      : 0;

    return { labels: dateRange, actualSeries, expectedSeries, actualBreakdown, expectedBreakdown, subBreakActual, subBreakExpected, subcats, dateRange, stats: { sum, avg, stdDev } };
  }, [actuals, projected, aggregation, fromDate, toDate, useGroups, preferAssetExpenseTypes, showExpected, showProjectedRecurring, showRecurring, hiddenSubcats, showSubcats, rollupHome]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      if (typeof onRangeChange === 'function') {
        onRangeChange(fromDate, toDate);
      }
    } catch (e) { /* noop */ }
  }, [fromDate, toDate, onRangeChange]);

  const cumulate = (arr) => {
    const out = [];
    let run = 0;
    for (let i = 0; i < arr.length; i++) {
      run += Number(arr[i]) || 0;
      out.push(run);
    }
    return out;
  };

  // Helpers shared by data useMemo and computeBudgetTableRows
  const toTitle = useCallback((code) => {
    if (!code) return '';
    if (code === 'recurring') return 'Recurring';
    if (code === 'non_recurring') return 'Non-recurring';
    const hierLabels = categoryHierarchy?.labels || {};
    if (hierLabels[code]) return hierLabels[code];
    return code.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }, [categoryHierarchy]);

  // Budget series: per-period sum of all category budget amounts from budgetData
  const { budgetSeries, budgetCategoryBreakdown } = useMemo(() => {
    if (!budgetData || budgetData.length === 0 || !dateRange || dateRange.length === 0) {
      return { budgetSeries: dateRange ? dateRange.map(() => 0) : [], budgetCategoryBreakdown: [] };
    }
    const breakdown = dateRange.map((d) => {
      const mKey = moment(d).format('YYYY-MM');
      const catMap = {};
      for (const bud of budgetData) {
        const bStart = moment(bud.start_date).startOf('month');
        const bEnd   = moment(bud.end_date).endOf('month');
        if (moment(d).isBefore(bStart) || moment(d).isAfter(bEnd)) continue;
        const cma = bud.category_monthly_amounts || {};
        for (const [slug, slugAmounts] of Object.entries(cma)) {
          const rawVal = Number((slugAmounts || {})[mKey] || 0);
          let val = rawVal;
          if (aggregation === 'daily')  val = rawVal / moment(d).daysInMonth();
          else if (aggregation === 'weekly') val = rawVal / 4.33;
          else if (aggregation === 'yearly') {
            const yr = moment(d).format('YYYY');
            val = Object.entries(slugAmounts || {}).filter(([mk]) => mk.startsWith(yr)).reduce((s,[,mv]) => s + (Number(mv)||0), 0);
          }
          catMap[slug] = (catMap[slug] || 0) + val;
        }
      }
      return catMap;
    });
    const series = breakdown.map(catMap => Object.values(catMap).reduce((s, v) => s + v, 0));
    return { budgetSeries: series, budgetCategoryBreakdown: breakdown };
  }, [budgetData, dateRange, aggregation]); // eslint-disable-line react-hooks/exhaustive-deps

  const budgetStats = useMemo(() => {
    if (!budgetSeries || budgetSeries.every(v => v === 0)) return null;
    const budgetSum = budgetSeries.reduce((a, b) => a + b, 0);
    const actualSum = (stats && stats.sum) || 0;
    const variance  = actualSum - budgetSum;
    const pct       = budgetSum > 0 ? ((variance / budgetSum) * 100).toFixed(1) : null;
    return { budgetSum, variance, pct };
  }, [budgetSeries, stats]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = useMemo(() => {
    // Color palette for known sub-categories
    const PALETTE = {
      maintenance: '#60A5FA',
      repair: '#F87171',
      enhancement: '#34D399',
      taxes: '#F59E0B',
      insurance: '#A78BFA',
      ticket_or_fine: '#FB7185',
      asset_input: '#22D3EE',
      asset_storage: '#93C5FD',
      licensing_tags: '#60A5FA',
      initial_purchase: '#4ADE80',
      Operating: '#3B82F6',
      Financing: '#F59E0B',
      CapEx: '#10B981',
      uncategorized: '#9CA3AF',
      recurring: '#3B82F6',
      non_recurring: '#6B7280',
    };
    const fallbackColors = ['#3B82F6','#EF4444','#10B981','#F59E0B','#8B5CF6','#06B6D4','#A3E635','#F472B6','#22C55E','#EAB308'];
    const colorFor = (cat, idx) => PALETTE[cat] || fallbackColors[idx % fallbackColors.length];

    if (showSubcats || useGroups || showRecurring) {
      // Build grouped stacks: one stack for Actual subcats, one for Expected subcats
      const datasets = [];
      const sortedSubcats = [...(subcats || [])].sort();
      sortedSubcats.forEach((cat, i) => {
        const arrA = subBreakActual?.[cat] || [];
        const arrE = showExpected ? (subBreakExpected?.[cat] || []) : [];
        const dsA = cumulative ? cumulate(arrA) : arrA;
        const dsE = cumulative ? cumulate(arrE) : arrE;
        const color = colorFor(cat, i);
        datasets.push({
          label: `Actual • ${toTitle(cat)}`,
          data: dsA,
          backgroundColor: color,
          stack: 'actual',
          order: 1,
        });
        if (showExpected) {
          datasets.push({
            label: `Expected • ${toTitle(cat)}`,
            data: dsE,
            backgroundColor: color + '80', // translucent same color
            stack: 'expected',
            order: 2,
          });
        }
      });
      return { labels, datasets };
    }

    // Default: two simple bars (Actual vs Expected)
    const act = cumulative ? cumulate(actualSeries) : actualSeries;
    const exp = (showExpected || showProjectedRecurring) ? (cumulative ? cumulate(expectedSeries) : expectedSeries) : [];
    // Per-bar colors: gray out non-selected bars when a selection is active
    const hasSelection = selectedBarIndices && selectedBarIndices.size > 0;
    const barCount = act.length;
    const isSelected = (i) => {
      if (!hasSelection) return true;
      if (cumulative) {
        // In cumulative mode, selected bar means all bars up to and including it
        const maxSelected = Math.max(...Array.from(selectedBarIndices));
        return i <= maxSelected;
      }
      return selectedBarIndices.has(i);
    };
    const actBg = Array.from({ length: barCount }, (_, i) => isSelected(i) ? 'rgba(59,130,246,0.6)' : 'rgba(100,116,139,0.2)');
    const actBorder = Array.from({ length: barCount }, (_, i) => isSelected(i) ? 'rgba(59,130,246,1)' : 'rgba(100,116,139,0.4)');
    const expBg = Array.from({ length: barCount }, (_, i) => isSelected(i) ? 'rgba(234,179,8,0.5)' : 'rgba(100,116,139,0.2)');
    const expBorder = Array.from({ length: barCount }, (_, i) => isSelected(i) ? 'rgba(234,179,8,1)' : 'rgba(100,116,139,0.4)');
    return {
      labels,
      datasets: [
        {
          label: 'Actual',
          data: act,
          backgroundColor: actBg,
          borderColor: actBorder,
          borderWidth: 1,
          order: 1,
        },
        ...((showExpected || showProjectedRecurring) ? [{
          label: showProjectedRecurring ? 'Projected' : 'Expected',
          data: exp,
          backgroundColor: expBg,
          borderColor: expBorder,
          borderWidth: 1,
          order: 2,
        }] : []),
        ...(budgetSeries && budgetSeries.some(v => v > 0) ? [{
          label: 'Budget',
          data: cumulative ? cumulate(budgetSeries) : budgetSeries,
          backgroundColor: 'rgba(52,211,153,0.25)',
          borderColor: 'rgba(52,211,153,0.9)',
          borderWidth: 2,
          type: 'line',
          fill: false,
          pointRadius: 3,
          order: 0,
        }] : []),
      ],
    };
  }, [labels, actualSeries, expectedSeries, cumulative, showSubcats, useGroups, subcats, subBreakActual, subBreakExpected, showExpected, showProjectedRecurring, budgetSeries, showRecurring, toTitle, selectedBarIndices]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCurrency = (val) => {
    const n = Number(val) || 0;
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const ACTUAL_DEBT_SLUGS = new Set(['financing_interest','financing_principal','interest','principal',
    'property_taxes','taxes','insurance','financing_inter','debt_service','payments']);
  const consolidateDebtSlugs = (map) => {
    const out = {};
    let debtTotal = 0;
    for (const [k, v] of Object.entries(map)) {
      const slugKey = String(k).toLowerCase().replace(/[^a-z0-9_]+/g,'_');
      // 'debt_payment' is the consolidated output key — never roll it up again into debtTotal
      if (slugKey === 'debt_payment') {
        out[k] = (out[k] || 0) + (Number(v) || 0);
      } else if (ACTUAL_DEBT_SLUGS.has(slugKey) || slugKey.startsWith('payments__debt') || slugKey.startsWith('debt_')) {
        debtTotal += Number(v) || 0;
      } else {
        out[k] = v;
      }
    }
    if (debtTotal > 0) out['debt_payment'] = (out['debt_payment'] || 0) + debtTotal;
    return out;
  };

  // Compute budget table rows for selected period indices (or all if empty).
  // Returns { rows, actTotal, budTotal, periodLabel } or null.
  const computeBudgetTableRows = (indices) => {
    if (!budgetCategoryBreakdown || !budgetData || budgetData.length === 0) return null;
    const hierParents = categoryHierarchy?.parents || {};
    const hierLabels  = categoryHierarchy?.labels  || {};
    const getLabel = (slug) => hierLabels[slug] || toTitle(slug);

    const allIndices  = (dateRange || []).map((_, i) => i);
    const useIndices  = (indices && indices.size > 0) ? Array.from(indices).sort((a,b)=>a-b) : allIndices;

    const rawBudgetMap = useIndices.reduce((acc, i) => {
      for (const [k, v] of Object.entries(budgetCategoryBreakdown[i] || {})) acc[k] = (acc[k]||0)+(Number(v)||0);
      return acc;
    }, {});
    if (Object.keys(rawBudgetMap).length === 0) return null;

    const aMap = useIndices.reduce((acc, i) => {
      const lbl = labels[i];
      for (const [k, v] of Object.entries(actualBreakdown?.[lbl] || {})) acc[k] = (acc[k]||0)+(Number(v)||0);
      return acc;
    }, {});

    const budgetMap = consolidateDebtSlugs(rawBudgetMap);
    const actualMap = consolidateDebtSlugs(aMap);

    // Build bidirectional slug→parent map
    const slugToParent = {};
    const slugToChildLabel = {};
    for (const [parent, info] of Object.entries(hierParents)) {
      for (const ch of info.children) {
        slugToParent[ch.value] = parent;
        slugToChildLabel[ch.value] = ch.label;
        const prefix2 = parent + '__';
        const prefix = parent + '_';
        if (ch.value.startsWith(prefix2)) {
          const s2 = ch.value.slice(prefix2.length);
          if (s2 && !s2.startsWith('_') && !slugToParent[s2]) { slugToParent[s2]=parent; slugToChildLabel[s2]=ch.label; }
        } else if (ch.value.startsWith(prefix)) {
          const s = ch.value.slice(prefix.length);
          if (s && !s.startsWith('_') && !slugToParent[s]) { slugToParent[s]=parent; slugToChildLabel[s]=ch.label; }
        }
      }
    }
    // canonical normalization: stripped → full slug
    const toCanonical = {};
    for (const [parent, info] of Object.entries(hierParents)) {
      for (const ch of info.children) {
        const prefix2 = parent+'__', prefix = parent+'_';
        if (ch.value.startsWith(prefix2)) { const s=ch.value.slice(prefix2.length); if(s&&!s.startsWith('_')&&!toCanonical[s]) toCanonical[s]=ch.value; }
        else if (ch.value.startsWith(prefix)) { const s=ch.value.slice(prefix.length); if(s&&!s.startsWith('_')&&!toCanonical[s]) toCanonical[s]=ch.value; }
      }
    }

    const KNOWN_LEAF_PARENTS = new Set(['landscaping']);
    const filterByParent = (slug) => {
      if (!allowedBudgetParents || !allowedBudgetParents.length) return true;
      if (allowedBudgetParents.includes(slug)) return true;
      const par = slugToParent[slug];
      return par ? allowedBudgetParents.includes(par) : false;
    };

    const normalizedActualMap = {};
    for (const [k, v] of Object.entries(actualMap)) {
      // Only use canonical (full) slug if the budget doesn't already have the bare key
      const can = (toCanonical[k] && !budgetMap[k]) ? toCanonical[k] : k;
      normalizedActualMap[can] = (normalizedActualMap[can]||0) + v;
    }

    // Collect actuals that don't match any allowedBudgetParents — will surface as "Un-mapped" breakdown
    let unmappedActualTotal = 0;
    const unmappedByCategory = {}; // slug → amount
    if (allowedBudgetParents && allowedBudgetParents.length) {
      for (const [k, v] of Object.entries(normalizedActualMap)) {
        if (!filterByParent(k)) {
          unmappedActualTotal += v;
          unmappedByCategory[k] = (unmappedByCategory[k] || 0) + v;
        }
      }
    }
    const allKeys = new Set([...Object.keys(normalizedActualMap),...Object.keys(budgetMap)].filter(filterByParent));
    const flatEntries = {};
    for (const k of allKeys) {
      const a = Number(normalizedActualMap[k]||0), b = Number(budgetMap[k]||0);
      if (a===0 && b===0) continue;
      flatEntries[k] = [a,b];
    }
    if (Object.keys(flatEntries).length === 0) return null;

    const PRIORITY_ORDER = ['debt_payment','home','utilities','landscaping'];
    const parentBuckets = {};
    const unparentedEntries = {};

    for (const [slug, [a,b]] of Object.entries(flatEntries)) {
      if (slug === 'debt_payment') { unparentedEntries[slug]=[a,b]; continue; }
      const parentSlug = slugToParent[slug];
      if (parentSlug && hierParents[parentSlug]) {
        if (!parentBuckets[parentSlug]) parentBuckets[parentSlug]={label:hierParents[parentSlug].label,children:[],directA:0,directB:0};
        parentBuckets[parentSlug].children.push({slug,label:slugToChildLabel[slug]||getLabel(slug),a,b});
      } else if (hierParents[slug]||KNOWN_LEAF_PARENTS.has(slug)) {
        const lbl = hierParents[slug]?.label||hierLabels[slug]||toTitle(slug);
        if (!parentBuckets[slug]) parentBuckets[slug]={label:lbl,children:[],directA:0,directB:0};
        parentBuckets[slug].directA+=a; parentBuckets[slug].directB+=b;
      } else {
        unparentedEntries[slug]=[a,b];
      }
    }

    const rows = [];
    const addGroup = (ps) => {
      const bkt = parentBuckets[ps]; if (!bkt) return;
      const tA=bkt.directA+bkt.children.reduce((s,c)=>s+c.a,0);
      const tB=bkt.directB+bkt.children.reduce((s,c)=>s+c.b,0);
      if (tA===0&&tB===0) return;
      rows.push({label:bkt.label,a:tA,b:tB,isParent:true,indent:false});
      [...bkt.children].sort((x,y)=>y.b-x.b).forEach(ch=>rows.push({label:ch.label,a:ch.a,b:ch.b,isParent:false,indent:true}));
    };

    if (unparentedEntries['debt_payment']) {
      const [a,b]=unparentedEntries['debt_payment'];
      rows.push({label:'Debt Payment',a,b,isParent:true,indent:false});
      delete unparentedEntries['debt_payment'];
    }
    PRIORITY_ORDER.slice(1).forEach(ps=>addGroup(ps));
    Object.keys(parentBuckets).filter(ps=>!PRIORITY_ORDER.includes(ps))
      .sort((a,b)=>(parentBuckets[a].label||a).localeCompare(parentBuckets[b].label||b))
      .forEach(ps=>addGroup(ps));
    Object.entries(unparentedEntries).sort((a,b)=>b[1][1]-a[1][1])
      .forEach(([slug,[a,b]])=>rows.push({label:getLabel(slug),a,b,isParent:false,indent:false}));
    // Add Un-mapped section so budget table total reconciles with chart total
    if (unmappedActualTotal > 0) {
      rows.push({label:'Un-mapped (not in budget)',a:unmappedActualTotal,b:0,isParent:true,indent:false,isUnmapped:true});
      Object.entries(unmappedByCategory)
        .sort((a,b) => b[1]-a[1])
        .forEach(([slug, amt]) => rows.push({label:getLabel(slug),a:amt,b:0,isParent:false,indent:true,isUnmapped:true}));
    }
    const actTotal = Object.values(flatEntries).reduce((s,[a])=>s+a,0) + unmappedActualTotal;
    const budTotal = Object.values(flatEntries).reduce((s,[,b])=>s+b,0);

    let periodLabel;
    if (indices && indices.size > 0) {
      const sorted = Array.from(indices).sort((a,b)=>a-b);
      periodLabel = sorted.map(i=>labels[i]).join(', ');
    } else {
      periodLabel = `${fromDate} to ${toDate}`;
    }
    return { rows, actTotal, budTotal, periodLabel };
  };

  // Stable serialized key for actualBreakdown — changes only when actual data changes, not on every render
  const actualBreakdownKey = useMemo(() => JSON.stringify(actualBreakdown), [actualBreakdown]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep a stable ref to onBudgetTableChange so it doesn't trigger the effect on every render
  const onBudgetTableChangeRef = useRef(onBudgetTableChange);
  useEffect(() => { onBudgetTableChangeRef.current = onBudgetTableChange; });

  // Notify parent whenever budget data or bar selection changes
  useEffect(() => {
    if (typeof onBudgetTableChangeRef.current !== 'function') return;
    if (!budgetData || budgetData.length === 0) { onBudgetTableChangeRef.current(null); return; }
    const tableData = computeBudgetTableRows(selectedBarIndices);
    onBudgetTableChangeRef.current(tableData);
  }, [budgetData, selectedBarIndices, actualBreakdownKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#e5e7eb' },
        onClick: (e, legendItem, legend) => {
          const index = legendItem.datasetIndex;
          const ci = legend.chart;
          if (ci.isDatasetVisible(index)) {
            ci.hide(index);
            legendItem.hidden = true;
          } else {
            ci.show(index);
            legendItem.hidden = false;
          }

          // Also update our React state to trigger stats recalculation
          const label = legendItem.text;
          // Legend labels are in format "Actual • CategoryName" or "Expected • CategoryName"
          if (label.includes(' • ')) {
            const catNameRaw = label.split(' • ')[1];
            // We need to find the original category code from subcats by reverse mapping toTitle
            const catCode = subcats.find(c => toTitle(c) === catNameRaw);
            if (catCode) {
              setHiddenSubcats(prev => {
                const next = new Set(prev);
                if (next.has(catCode)) next.delete(catCode);
                else next.add(catCode);
                return next;
              });
            }
          } else if (label === 'Actual') {
             setHiddenSubcats(prev => {
                const next = new Set(prev);
                if (next.has('__ALL_ACTUAL__')) next.delete('__ALL_ACTUAL__');
                else next.add('__ALL_ACTUAL__');
                return next;
             });
          }
        }
      },
      title: { display: false },
      tooltip: {
        backgroundColor: '#111827',
        titleColor: '#e5e7eb',
        bodyColor: '#e5e7eb',
        titleFont: { family: 'monospace' },
        bodyFont: { family: 'monospace' },
        callbacks: {
          title: (items) => {
            if (!items || items.length === 0) return '';
            const i = items[0].dataIndex;
            return labels[i] || '';
          },
          afterBody: (items) => {
            try {
              if (!items || items.length === 0) return [];
              const i = items[0].dataIndex;
              const period = labels[i];
              const aMap = actualBreakdown?.[period] || {};
              const eMap = expectedBreakdown?.[period] || {};
              const dsIndex = items[0].datasetIndex ?? 0;
              const chart = items[0].chart;
              const datasets = chart?.data?.datasets || [];

              // Helper: determine if a given sub-category's Actual/Expected dataset is visible
              const labelFor = (cat, kind) => `${kind} • ${toTitle(cat)}`;
              const isVisible = (cat, kind) => {
                const lbl = labelFor(cat, kind);
                const idx = datasets.findIndex(d => String(d?.label||'') === lbl);
                if (idx < 0) return false; // dataset not present
                const meta = chart.getDatasetMeta(idx);
                return !meta?.hidden; // true if visible
              };
              const isStacked = showSubcats || useGroups || showRecurring || cumulative;
              // Helper: sum array up to index i inclusive
              const sumTo = (arr, idx) => {
                let s = 0;
                for (let j = 0; j <= idx && j < arr.length; j++) s += Number(arr[j]) || 0;
                return s;
              };
              // Build the map used for tooltip rows
              let mapToUse = {};
              if (cumulative) {
                // In cumulative mode, show cumulative totals up to index i by sub-category.
                const showCombined = showSubcats || isStacked; // maintain combined behavior when stacked/subcats
                if (showCombined) {
                  // Actual + (optional) Expected cumulative per subcat
                  (subcats || []).forEach((cat) => {
                    let val = 0;
                    const arrA = subBreakActual?.[cat] || [];
                    const arrE = subBreakExpected?.[cat] || [];
                    // Include Actual portion only if its dataset for this cat is visible
                    if (isVisible(cat, 'Actual')) val += sumTo(arrA, i);
                    // Include Expected portion only if showExpected and its dataset is visible
                    if (showExpected && isVisible(cat, 'Expected')) val += sumTo(arrE, i);
                    if (val) mapToUse[cat] = val;
                  });
                } else {
                  // Only the hovered dataset's cumulative per subcat
                  const ds = items[0].chart?.data?.datasets?.[dsIndex];
                  const isExpected = (ds && typeof ds.label === 'string' && ds.label.toLowerCase().startsWith('expected')) || (ds?.stack === 'expected');
                  (subcats || []).forEach((cat) => {
                    const arr = isExpected ? (showExpected ? (subBreakExpected?.[cat] || []) : []) : (subBreakActual?.[cat] || []);
                    const val = sumTo(arr, i);
                    if (val) mapToUse[cat] = val;
                  });
                }
              } else {
                // Non-cumulative mode: use per-period breakdown maps
                const showCombined = showSubcats || isStacked;
                if (showCombined) {
                  const combined = {};
                  // Build combined from visible datasets only
                  (subcats || []).forEach((cat) => {
                    const a = Number(aMap[cat] || 0);
                    const e = Number(eMap[cat] || 0);
                    let val = 0;
                    if (isVisible(cat, 'Actual')) val += a;
                    if (showExpected && isVisible(cat, 'Expected')) val += e;
                    if (val) combined[cat] = val;
                  });
                  mapToUse = combined;
                } else {
                  mapToUse = dsIndex === 0 ? aMap : (showExpected ? eMap : {});
                }
              }
              // Compute total and rows using only visible subcats
              const entriesAll = Object.entries(mapToUse);
              const entriesVisible = entriesAll.filter(([k]) => {
                // In combined mode, keep if either Actual or Expected dataset for this cat is visible
                if (showSubcats || isStacked) {
                  return isVisible(k, 'Actual') || (showExpected && isVisible(k, 'Expected'));
                }
                return true;
              });
              const total = entriesVisible.reduce((s, [,v]) => s + (Number(v)||0), 0);
              if (total <= 0) return [];
              const entries = entriesVisible.sort((a,b) => b[1]-a[1]);
              const nameLens = entries.map(([k]) => toTitle(k).length);
              const valLens = entries.map(([,v]) => formatCurrency(v).length);
              const maxName = nameLens.length ? Math.max(...nameLens) : 0;
              const maxVal = valLens.length ? Math.max(...valLens) : 0;
              const rows = entries.map(([k, v]) => {
                const pct = total ? ((Number(v)/total)*100) : 0;
                const name = toTitle(k).padEnd(maxName, ' ');
                const valStr = formatCurrency(v).padStart(maxVal, ' ');
                const pctStr = `${pct.toFixed(1)}%`;
                return `${name}  ${valStr}  (${pctStr})`;
              });

              // Compute budget (expected) total for this period
              const budgetTotal = Object.values(eMap).reduce((s, v) => s + (Number(v) || 0), 0);
              // Also sum from expectedSeries for this index as fallback
              const budgetFromSeries = Number(expectedSeries?.[i] || 0);
              const budgetAmt = budgetTotal > 0 ? budgetTotal : budgetFromSeries;
              const diff = total - budgetAmt;
              const diffSign = diff > 0 ? '+' : '';
              const labelWidth = Math.max(maxName, 'Actual Total'.length, 'Budget Total'.length, 'Over/Under Budget'.length);
              const valWidth = Math.max(maxVal, formatCurrency(budgetAmt).length, formatCurrency(diff).length);
              rows.push('');
              rows.push(`${'Actual Total'.padEnd(labelWidth, ' ')}  ${formatCurrency(total).padStart(valWidth, ' ')}`);
              if (budgetAmt > 0) {
                rows.push(`${'Budget Total'.padEnd(labelWidth, ' ')}  ${formatCurrency(budgetAmt).padStart(valWidth, ' ')}`);
                rows.push('─'.repeat(labelWidth + valWidth + 2));
                rows.push(`${'Over/Under Budget'.padEnd(labelWidth, ' ')}  ${(diffSign + formatCurrency(Math.abs(diff))).padStart(valWidth, ' ')}`);
              }
              return rows;
            } catch (e) {
              return [];
            }
          }
        }
      },
    },
    scales: {
      x: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' }, stacked: showSubcats || useGroups || showRecurring || cumulative },
      y: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' }, stacked: showSubcats || useGroups || showRecurring || cumulative },
    },
    onClick: (event, elements) => {
      if (!elements || elements.length === 0) return;
      const clickedIdx = elements[0].index;
      setSelectedBarIndices(prev => {
        const next = new Set(prev);
        if (next.has(clickedIdx)) next.delete(clickedIdx); else next.add(clickedIdx);
        if (typeof onRangeChange === 'function') {
          if (next.size === 0) {
            onRangeChange(fromDate, toDate);
          } else {
            const sorted = Array.from(next).sort((a,b)=>a-b);
            const startD = dateRange[sorted[0]];
            const lastBarStart = dateRange[sorted[sorted.length-1]];
            // Compute the end of the last selected bar's period
            const unit = aggregation === 'weekly' ? 'week' : aggregation === 'yearly' ? 'year' : aggregation === 'daily' ? 'day' : 'month';
            const endD = moment(lastBarStart).endOf(unit).format('YYYY-MM-DD');
            onRangeChange(startD, endD);
          }
        }
        if (typeof onBarSelectionChange === 'function') {
          onBarSelectionChange({ selectedIndices: next, dateRange, labels, cumulative });
        }
        return next;
      });
    },
  }), [labels, data, cumulative, actualBreakdown, expectedBreakdown, expectedSeries, showSubcats, useGroups, showRecurring, showExpected, subcats, subBreakActual, subBreakExpected, selectedBarIndices, fromDate, toDate, dateRange, onRangeChange, toTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ height: 400, background: '#0b1220', border: '1px solid #374151', borderRadius: 8, padding: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent:'space-between', marginBottom: 8 }}>
        {/* Left: Title with period/date controls inline to its right */}
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'nowrap', whiteSpace:'nowrap', flex: '1 1 auto', minWidth: 0 }}>
          <div style={{ color: '#e5e7eb', fontWeight: 600, whiteSpace:'nowrap', flexShrink: 0 }}>{chartTitle}</div>
          <select className="form-select form-select-sm" value={aggregation} onChange={(e) => setAggregation(e.target.value)} style={{ width: 120, flexShrink: 0 }}>
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="daily">Daily</option>
            <option value="yearly">Yearly</option>
          </select>
          <input className="form-control form-control-sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ width: 130, flexShrink: 0 }} />
          <input className="form-control form-control-sm" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ width: 130, flexShrink: 0 }} />
        </div>
        {/* Right: Toggles group */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, fontSize: '12px' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <input className="form-check-input" type="checkbox" id="groupSwitch" checked={useGroups} onChange={(e)=> setUseGroups(e.target.checked)} />
              <label className="form-check-label" htmlFor="groupSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Show by Type</label>
            </div>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <input className="form-check-input" type="checkbox" id="recurringSwitch" checked={showRecurring} onChange={(e)=> setShowRecurring(e.target.checked)} />
              <label className="form-check-label" htmlFor="recurringSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Show Recurring</label>
            </div>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <input className="form-check-input" type="checkbox" id="subcatSwitch" checked={showSubcats} onChange={(e)=> setShowSubcats(e.target.checked)} />
              <label className="form-check-label" htmlFor="subcatSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Show by Sub-Categories</label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <input className="form-check-input" type="checkbox" id="cumSwitch" checked={cumulative} onChange={(e)=> setCumulative(e.target.checked)} />
              <label className="form-check-label" htmlFor="cumSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Cumulative</label>
            </div>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4, opacity: 0.5 }}>
              <input className="form-check-input" type="checkbox" id="expectedSwitch" checked={!!showExpected} disabled />
              <label className="form-check-label" htmlFor="expectedSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Show Expected</label>
            </div>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <input className="form-check-input" type="checkbox" id="projectedRecurringSwitch" checked={showProjectedRecurring} onChange={(e)=> setShowProjectedRecurring(e.target.checked)} />
              <label className="form-check-label" htmlFor="projectedRecurringSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Show Projected Recurring</label>
            </div>
            {extraRightControls}
          </div>
        </div>
      </div>
      {budgetLoading && <div style={{ color:'#34d399', fontSize:'12px', marginBottom:4 }}>Loading budget…</div>}
      {selectedBarIndices.size > 0 && (
        <div style={{ color:'#fbbf24', fontSize:'12px', marginBottom:4 }}>
          📌 Period filter active: {Array.from(selectedBarIndices).sort((a,b)=>a-b).map(i=>labels[i]).join(', ')}
          {' '}<button onClick={() => { setSelectedBarIndices(new Set()); if (typeof onRangeChange === 'function') onRangeChange(fromDate, toDate); }} style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:'11px' }}>✕ Clear</button>
        </div>
      )}
      {stats && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 16, color: '#9ca3af', fontSize: '13px', marginBottom: 8, paddingLeft: 4 }}>
          <span>Sum: <strong style={{ color: '#e5e7eb' }}>${stats.sum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
          <span>Avg: <strong style={{ color: '#e5e7eb' }}>${stats.avg.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
          <span>Std Dev: <strong style={{ color: '#e5e7eb' }}>${stats.stdDev.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
        </div>
      )}
      {budgetStats && (
        <div style={{ display:'flex', gap:16, color:'#9ca3af', fontSize:'13px', marginBottom:4, paddingLeft:4 }}>
          <span>Budget: <strong style={{ color:'#34d399' }}>${budgetStats.budgetSum.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></span>
          <span>Variance (Actual−Budget): <strong style={{ color: budgetStats.variance > 0 ? '#f87171' : '#34d399' }}>{budgetStats.variance >= 0 ? '+' : ''}{budgetStats.variance.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}{budgetStats.pct !== null ? ` (${budgetStats.variance >= 0 ? '+' : ''}${budgetStats.pct}%)` : ''}</strong></span>
        </div>
      )}
      {loading ? (
        <div style={{ color: '#9ca3af' }}>Loading…</div>
      ) : error ? (
        <div className="alert alert-warning" style={{ background: 'transparent', color: '#fbbf24' }}>{error}</div>
      ) : (
        <div style={{ height: stats ? 310 : 340 }}>
          <Bar ref={chartRef} data={data} options={options} height={stats ? 310 : 340} />
        </div>
      )}
    </div>
  );
};

export default AggregatedAssetExpenseChart;
